import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Workspace, WorkspaceVisibility } from '../../types/common';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { updateWorkspace } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import './WorkspaceModal.scss';

interface EditWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  workspace: Workspace | null;
}

const workspaceColors = [
  '#0073ea', // Blue
  '#ff5a5f', // Red
  '#00ca72', // Green
  '#9d77f9', // Purple
  '#ffcb00', // Yellow
  '#ff6900', // Orange
  '#ff1493', // Pink
  '#00d4ff', // Cyan
];

const EditWorkspaceModal: React.FC<EditWorkspaceModalProps> = ({ open, onClose, workspace }) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.workspaces);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility: WorkspaceVisibility.TEAM,
    color: '#0073ea',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  useEffect(() => {
    if (workspace) {
      setFormData({
        name: workspace.name,
        description: workspace.description || '',
        visibility: workspace.visibility,
        color: workspace.color || '#0073ea',
      });
    }
  }, [workspace]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name' && errors.name) {
      setErrors((prev) => ({ ...prev, name: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Workspace name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Workspace name must be at least 3 characters';
    }

    setErrors(newErrors);
    return !newErrors.name;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !workspace) {
      return;
    }

    try {
      await dispatch(
        updateWorkspace({
          workspaceId: workspace.id,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
            visibility: formData.visibility,
            color: formData.color,
          },
        })
      ).unwrap();

      dispatch(
        showToast({
          message: 'Workspace updated successfully!',
          type: 'success',
        })
      );

      onClose();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error.message || 'Failed to update workspace',
          type: 'error',
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Workspace</DialogTitle>
      <DialogContent>
        <Box className="workspace-modal-content">
          <TextField
            fullWidth
            label="Workspace Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g., Product Development"
            autoFocus
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="What is this workspace for?"
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={formData.visibility}
              onChange={(e) => handleInputChange('visibility', e.target.value)}
              label="Visibility"
            >
              <MenuItem value={WorkspaceVisibility.PRIVATE}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Private
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only you can access this workspace
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={WorkspaceVisibility.TEAM}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Team
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Members can access and collaborate
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={WorkspaceVisibility.PUBLIC}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Public
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anyone in the organization can view
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Workspace Color
            </Typography>
            <Box className="color-picker">
              {workspaceColors.map((color) => (
                <Box
                  key={color}
                  className={`color-option ${formData.color === color ? 'selected' : ''}`}
                  onClick={() => handleInputChange('color', color)}
                  sx={{
                    backgroundColor: color,
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditWorkspaceModal;
